import { useCallback } from 'react';
import { CombinedMediaItem } from '../types';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';

export interface AttachmentSizeLimits {
    pdfMaxPreviewSizeBytes: number;
    imageMaxPreviewSizeBytes: number;
    videoMaxPreviewSizeBytes: number;
    thumbnailMaxSizeBytes: number;
    getMaxPreviewSizeForItem: (item: CombinedMediaItem) => number | null;
}

// Default values in MB (used as fallback if bootstrap settings are not available)
export const DEFAULT_PDF_MAX_SIZE_MB = 15;
export const DEFAULT_IMAGE_MAX_SIZE_MB = 40;
export const DEFAULT_VIDEO_MAX_SIZE_MB = 100;
// Fallback: use original as thumbnail if no large thumbnail exists and original file is below this size
export const DEFAULT_THUMBNAIL_FALLBACK_MAX_SIZE_MB = 2;

const MB_TO_BYTES = 1000 * 1000;

/**
 * Converts megabytes to bytes using decimal units (1 MB = 1,000,000 bytes).
 * This matches the file size display format used in file systems and browsers.
 *
 * @param megabytes - The size in megabytes (MB)
 * @returns The size in bytes (e.g., 100 MB returns 100,000,000 bytes)
 */
function megabytesToBytes(megabytes: number): number {
    return megabytes * MB_TO_BYTES;
}

/**
 * Hook to get attachment size limits for preview.
 *
 * PDFs and images exceeding these limits will not be rendered inline.
 * Users will be prompted to download them instead.
 *
 * @returns Size limits and helper functions to check if attachments can be previewed
 */
export function useAttachmentSizeLimits(): AttachmentSizeLimits {
    const bootstrap = useBootstrapState();

    // Get size limits in MB from bootstrap settings (with fallback to defaults)
    const pdfMaxPreviewSizeMB = bootstrap.attachmentPdfMaxPreviewSizeMB ?? DEFAULT_PDF_MAX_SIZE_MB;
    const imageMaxPreviewSizeMB =
        bootstrap.attachmentImageMaxPreviewSizeMB ?? DEFAULT_IMAGE_MAX_SIZE_MB;
    const videoMaxPreviewSizeMB =
        bootstrap.attachmentVideoMaxPreviewSizeMB ?? DEFAULT_VIDEO_MAX_SIZE_MB;
    const thumbnailMaxSizeMB =
        bootstrap.attachmentThumbnailMaxSizeMB ?? DEFAULT_THUMBNAIL_FALLBACK_MAX_SIZE_MB;

    // Convert MB to bytes
    const pdfMaxPreviewSizeBytes = megabytesToBytes(pdfMaxPreviewSizeMB);
    const imageMaxPreviewSizeBytes = megabytesToBytes(imageMaxPreviewSizeMB);
    const videoMaxPreviewSizeBytes = megabytesToBytes(videoMaxPreviewSizeMB);
    const thumbnailMaxSizeBytes = megabytesToBytes(thumbnailMaxSizeMB);

    const getMaxPreviewSizeForItem = useCallback(
        (item: CombinedMediaItem): number | null => {
            switch (item.type) {
                case 'pdf':
                    return pdfMaxPreviewSizeBytes;
                case 'image':
                    return imageMaxPreviewSizeBytes;
                case 'video':
                    return videoMaxPreviewSizeBytes;
                default:
                    return null;
            }
        },
        [pdfMaxPreviewSizeBytes, imageMaxPreviewSizeBytes, videoMaxPreviewSizeBytes]
    );

    return {
        pdfMaxPreviewSizeBytes,
        imageMaxPreviewSizeBytes,
        videoMaxPreviewSizeBytes,
        thumbnailMaxSizeBytes,
        getMaxPreviewSizeForItem,
    };
}
